import React, { useMemo, useState } from 'react';
import { TextField, Button, MenuItem } from '@mui/material';
import DrawerShell from './DrawerShell';
import { FieldGroup } from '../PanelComerciales.styles';
import { panelTheme } from '../PanelComerciales.theme';
import { Commercial } from '../PanelComerciales.types';
import { isFutureOrTodayDate } from '../utils/validators';
import { SellerConfigPatch } from 'src/redux/panel_comerciales/panel_comerciales.types';

interface BlockCommercialDrawerProps {
    open: boolean;
    onClose: () => void;
    commercials: Commercial[];
    period: string;
    onConfirm: (sellerId: number, patch: SellerConfigPatch, label: string) => void;
}

const REASONS = [
    { value: 'incapacidad', label: 'Incapacidad médica' },
    { value: 'vacaciones', label: 'Vacaciones' },
    { value: 'capacitacion', label: 'Capacitación' },
];

const BlockCommercialDrawer: React.FC<BlockCommercialDrawerProps> = ({
    open,
    onClose,
    commercials,
    period,
    onConfirm,
}) => {
    const [sellerId, setSellerId] = useState<number | ''>('');
    const [reason, setReason] = useState<string>('vacaciones');
    const [since, setSince] = useState<string>('');
    const [until, setUntil] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    const sellers = useMemo(() => commercials.filter((c) => c.active), [commercials]);

    const close = () => {
        setSellerId('');
        setReason('vacaciones');
        setSince('');
        setUntil('');
        setNotes('');
        setError(null);
        onClose();
    };

    const submit = () => {
        if (!sellerId) return setError('Selecciona un comercial');
        if (!isFutureOrTodayDate(since)) return setError('Fecha de inicio inválida');
        if (!isFutureOrTodayDate(until)) return setError('Fecha de fin inválida');

        const seller = commercials.find((c) => c.id === sellerId);
        if (!seller) return setError('Comercial no encontrado');

        const reasonLabel = REASONS.find((r) => r.value === reason)?.label ?? reason;

        onConfirm(
            sellerId as number,
            {
                period,
                state: reason as SellerConfigPatch['state'],
                state_since: since,
                state_until: until,
                state_reason: reasonLabel,
                notes: notes || undefined,
            },
            `${seller.name}: ${reasonLabel} ${since} → ${until}`,
        );
        close();
    };

    return (
        <DrawerShell
            open={open}
            title='Bloquear comercial'
            description='Completa la información y guarda los cambios.'
            onClose={close}
        >
            <FieldGroup>
                <label>Comercial</label>
                <TextField
                    size='small'
                    select
                    value={sellerId}
                    onChange={(e) => setSellerId(Number(e.target.value))}
                >
                    {sellers.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.name} ({s.team})
                        </MenuItem>
                    ))}
                </TextField>
            </FieldGroup>
            <FieldGroup>
                <label>Motivo</label>
                <TextField
                    size='small'
                    select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                >
                    {REASONS.map((r) => (
                        <MenuItem key={r.value} value={r.value}>
                            {r.label}
                        </MenuItem>
                    ))}
                </TextField>
            </FieldGroup>
            <FieldGroup>
                <label>Desde</label>
                <TextField
                    size='small'
                    type='date'
                    value={since}
                    onChange={(e) => setSince(e.target.value)}
                />
            </FieldGroup>
            <FieldGroup>
                <label>Hasta</label>
                <TextField
                    size='small'
                    type='date'
                    value={until}
                    onChange={(e) => setUntil(e.target.value)}
                />
            </FieldGroup>
            <FieldGroup>
                <label>Notas (opcional)</label>
                <TextField
                    size='small'
                    multiline
                    minRows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />
            </FieldGroup>
            {error ? <span style={{ color: panelTheme.colors.danger, fontSize: 12 }}>{error}</span> : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button onClick={close} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    onClick={submit}
                    variant='contained'
                    sx={{
                        textTransform: 'none',
                        background: panelTheme.colors.danger,
                        '&:hover': { background: '#B91C1C' },
                    }}
                >
                    Bloquear
                </Button>
            </div>
        </DrawerShell>
    );
};

export default BlockCommercialDrawer;
